import React, { useState, useEffect } from "react";
import {
  getExams,
  createExam,
  updateExam,
  deleteExam,
  getAllSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
  getVideosBySubject,
  getPdfsBySubject,
  getMockTestPdfsBySubject,
  createPdf,
  uploadPdf,
  updateVideo,
  updateVideoWithFile,
  updatePdf,
  updatePdfWithFile,
  deleteVideo,
  deletePdf,
  getContentStats,
  uploadVideo,
  updateSubjectPricingForExam,
  getMaterials,
  createMaterial,
  updateMaterial,
  deleteMaterial,
  getMockTests,
  createMockTest,
  updateMockTest,
  deleteMockTest
} from "@/services/admin.util";
import { getVideoStreamUrl } from "@/services/subjects.util";
import ContentStats from "./components/ContentStats";
import SubjectPanel from "./components/SubjectPanel";
import ExamPanel from "./components/ExamPanel";
import ExamForm from "./forms/ExamForm";
import SubjectForm from "./forms/SubjectForm";
import ContentForm from "./forms/ContentForm";
import ExamSubjectLinkForm from "./forms/ExamSubjectLinkForm";
import SubjectExamLinkForm from "./forms/SubjectExamLinkForm";
import MaterialPanel from "./components/MaterialPanel";
import MaterialForm from "./forms/MaterialForm";
import MaterialSubjectLinkForm from "./forms/MaterialSubjectLinkForm";
import MockTestPanel from "./components/MockTestPanel";
import MockTestForm from "./forms/MockTestForm";
import MockTestSubjectLinkForm from "./forms/MockTestSubjectLinkForm";
import SubjectMockTestLinkForm from "./forms/SubjectMockTestLinkForm";

const CourseManagement = () => {
  // Active tab: 'subjects', 'exams', 'materials', or 'mockTests'
  const [activeTab, setActiveTab] = useState('subjects');
  
  // Data state
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjectContent, setSubjectContent] = useState({});
  const [contentStats, setContentStats] = useState({
    totalExams: 0,
    totalSubjects: 0,
    totalVideos: 0,
    totalMaterials: 0,
    totalTests: 0
  });
  const [materials, setMaterials] = useState([]);
  const [mockTests, setMockTests] = useState([]);
  
  // UI state
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [expandedExams, setExpandedExams] = useState(new Set());
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Form states
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [showExamForm, setShowExamForm] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showSubjectLinkForm, setShowSubjectLinkForm] = useState(false);
  const [showSubjectMockTestLinkForm, setShowSubjectMockTestLinkForm] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [showMaterialLinkForm, setShowMaterialLinkForm] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [expandedMaterials, setExpandedMaterials] = useState(new Set());
  const [showMockTestForm, setShowMockTestForm] = useState(false);
  const [showMockTestLinkForm, setShowMockTestLinkForm] = useState(false);
  const [selectedMockTest, setSelectedMockTest] = useState(null);
  const [expandedMockTests, setExpandedMockTests] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null);
  const [currentSubjectForContent, setCurrentSubjectForContent] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  
  // Loading and messages
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fetch all subjects (not filtered by exam)
  const fetchAllSubjects = async () => {
    try {
      setLoading(true);
      const response = await getAllSubjects();
      const subjectsData = response.data || [];
      setSubjects(subjectsData);
      
      // Fetch content for all subjects to show counts by default
      if (subjectsData.length > 0) {
        // Fetch content for all subjects in parallel
        const contentPromises = subjectsData.map(subject => fetchContentForSubject(subject._id));
        await Promise.all(contentPromises);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setMessage({ type: 'error', text: 'Failed to fetch subjects' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all exams
  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await getExams();
      setExams(response.data || []);
    } catch (error) {
      console.error('Error fetching exams:', error);
      setMessage({ type: 'error', text: 'Failed to fetch exams' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch content stats
  const fetchContentStats = async () => {
    try {
      const stats = await getContentStats();
      setContentStats(stats);
    } catch (error) {
      console.error('Error fetching content stats:', error);
    }
  };

  // Fetch content for a subject (PDFs include both regular PDFs and mock test PDFs; we split by isMockTest)
  const fetchContentForSubject = async (subjectId, showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      // Fetch videos, regular PDFs (excludes mock test PDFs by default), and mock test PDFs separately
      const [videosResponse, regularPdfsResponse, mockTestPdfsResponse] = await Promise.all([
        getVideosBySubject(subjectId),
        getPdfsBySubject(subjectId), // This excludes mock test PDFs by default
        getMockTestPdfsBySubject(subjectId) // Fetch mock test PDFs
      ]);
      setSubjectContent(prev => ({
        ...prev,
        [subjectId]: {
          videos: videosResponse.data || [],
          pdfs: regularPdfsResponse.data || [],
          mockTests: mockTestPdfsResponse.data || []
        }
      }));
    } catch (error) {
      console.error('Error fetching content:', error);
      // Don't show error message when fetching for all subjects (to avoid spam)
      if (showLoading) {
        setMessage({ type: 'error', text: 'Failed to fetch content' });
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Toggle subject expansion
  const toggleSubjectExpansion = async (subjectId) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
      // Content should already be fetched, but refresh if needed
      if (!subjectContent[subjectId]) {
        await fetchContentForSubject(subjectId, true);
      }
    }
    setExpandedSubjects(newExpanded);
  };

  // Toggle exam expansion
  const toggleExamExpansion = async (examId) => {
    const newExpanded = new Set(expandedExams);
    if (newExpanded.has(examId)) {
      newExpanded.delete(examId);
    } else {
      newExpanded.add(examId);
      // Fetch content for all subjects in this exam when expanding
      const examSubjects = subjects.filter(s => 
        s.exams && s.exams.some(e => (e._id || e) === examId)
      );
      await Promise.all(
        examSubjects.map(subject => {
          if (!subjectContent[subject._id]) {
            return fetchContentForSubject(subject._id, false);
          }
          return Promise.resolve();
        })
      );
    }
    setExpandedExams(newExpanded);
  };

  // Subject CRUD handlers
  const handleSubjectSubmit = async (formData, isEdit = false) => {
    try {
      if (isEdit && editingItem) {
        await updateSubject(editingItem._id, formData);
        setMessage({ type: 'success', text: 'Subject updated successfully!' });
      } else {
        // Create subject - exams are optional
        // If exams array is provided, use first exam to create through exam route
        if (formData.exams && formData.exams.length > 0) {
          const firstExamId = formData.exams[0];
          // Remove exams from formData as it's handled separately
          const { exams, ...subjectData } = formData;
          await createSubject(firstExamId, subjectData);
          
          // If multiple exams, link them with pricing
          if (formData.exams.length > 1) {
            // Note: Additional exams will need to be linked separately with pricing
            // For now, we'll just create with the first exam
          }
        } else {
          // Create subject without exam
          await createSubject(null, formData);
        }
        setMessage({ type: 'success', text: 'Subject created successfully!' });
      }
      setShowSubjectForm(false);
      setEditingItem(null);
      await fetchAllSubjects();
      await fetchContentStats();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save subject' });
      return { success: false, error: error.message };
    }
  };

  // Exam CRUD handlers
  const handleExamSubmit = async (formData, isEdit = false) => {
    try {
      if (isEdit && editingItem) {
        await updateExam(editingItem._id, formData);
        setMessage({ type: 'success', text: 'Exam updated successfully!' });
      } else {
        await createExam(formData);
        setMessage({ type: 'success', text: 'Exam created successfully!' });
      }
      setShowExamForm(false);
      setEditingItem(null);
      await fetchExams();
      await fetchContentStats();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save exam' });
      return { success: false, error: error.message };
    }
  };

  // Link subject to exams with pricing
  const handleLinkSubjectToExams = async (subjectId, linkedExams) => {
    try {
      setLoading(true);
      
      // Update subject's exams array
      const examIds = linkedExams.map(e => e.examId);
      await updateSubject(subjectId, { exams: examIds });
      
      // Update/create pricing for each exam
      for (const link of linkedExams) {
        if (link.pricing) {
          await updateSubjectPricingForExam(subjectId, link.examId, link.pricing);
        }
      }
      
      setMessage({ type: 'success', text: 'Subject linked to exams successfully!' });
      setShowSubjectLinkForm(false);
      setSelectedSubject(null);
      await fetchAllSubjects();
      await fetchExams();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to link subject to exams' });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Link subjects to exam
  const handleLinkSubjects = async (examId, linkedSubjects) => {
    try {
      setLoading(true);
      
      // For each linked subject, ensure it's in the exam's subjects array
      for (const link of linkedSubjects) {
        const subject = link.subject;
        
        // Check if subject already belongs to this exam
        const belongsToExam = subject.exams && subject.exams.some(e => (e._id || e) === examId);
        
        if (!belongsToExam) {
          // Add exam to subject's exams array
          const updatedExams = [...(subject.exams || []), examId];
          await updateSubject(subject._id, { exams: updatedExams });
        }
      }
      
      // Also unlink subjects that were unchecked
      const linkedIds = linkedSubjects.map(s => s.subjectId);
      const allSubjectsInExam = subjects.filter(s => 
        s.exams && s.exams.some(e => (e._id || e) === examId)
      );
      
      for (const subject of allSubjectsInExam) {
        if (!linkedIds.includes(subject._id)) {
          // Remove exam from subject's exams array (can be empty)
          const updatedExams = (subject.exams || []).filter(e => (e._id || e) !== examId);
          await updateSubject(subject._id, { exams: updatedExams });
        }
      }
      
      setMessage({ type: 'success', text: 'Subjects linked to exam successfully!' });
      setShowLinkForm(false);
      setSelectedExam(null);
      await fetchAllSubjects();
      await fetchExams();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to link subjects' });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Content handlers
  const handleContentSubmit = async (formData, isEdit = false) => {
    if (!currentSubjectForContent) return { success: false };
    if (!formData) {
      console.error('formData is undefined in handleContentSubmit');
      return { success: false, error: 'Form data is missing' };
    }
    const contentType = formData.contentType;
    
    try {
      if (isEdit) {
        // EDITING EXISTING CONTENT - videoId is REQUIRED
        // Get video ID from formData or editingItem (formData takes precedence)
        const videoId = formData._id || (editingItem && editingItem._id);
        
        if (!videoId) {
          console.error('Video ID is missing for edit operation:', { 
            formData, 
            editingItem, 
            contentType,
            isEdit,
            formDataKeys: Object.keys(formData),
            editingItemKeys: editingItem ? Object.keys(editingItem) : 'editingItem is null'
          });
          throw new Error('Video ID is missing. Cannot update video. Please try editing again.');
        }
        
        if (contentType === 'video') {
          if (formData.file) {
            // Update existing video with new file
            console.log('Updating existing video with new file:', { videoId, title: formData.title });
            const uploadFormData = new FormData();
            uploadFormData.append('video', formData.file);
            uploadFormData.append('title', formData.title);
            uploadFormData.append('description', formData.description);
            uploadFormData.append('order', formData.order || 0);
            uploadFormData.append('isFree', formData.isFree || false);
            await updateVideoWithFile(videoId, uploadFormData);
          } else {
            // Update video metadata only (no new file)
            console.log('Updating video metadata only:', { videoId });
            await updateVideo(videoId, {
              title: formData.title,
              description: formData.description,
              order: formData.order || 0,
              isFree: formData.isFree || false
            });
          }
        } else if (contentType === 'pdf' || contentType === 'material') {
          // Handle both 'pdf' and 'material' content types
          // Materials are PDFs with isMaterial: true
          const isMaterial = contentType === 'material' || formData.isMaterial;
          
          if (!editingItem._id || editingItem._id.includes('pdf-')) {
            if (formData.file) {
              const fd = new FormData();
              fd.append('file', formData.file);
              fd.append('title', formData.title);
              fd.append('description', formData.description || '');
              fd.append('order', formData.order || 0);
              fd.append('isFree', formData.isFree || false);
              fd.append('isMaterial', isMaterial ? 'true' : 'false');
              await uploadPdf(currentSubjectForContent._id, fd);
            } else {
              await createPdf(currentSubjectForContent._id, {
                title: formData.title,
                description: formData.description,
                order: formData.order || 0,
                isFree: formData.isFree || false,
                isMaterial: isMaterial || false
              });
            }
          } else {
            // Updating existing PDF
            if (formData.file) {
              // If a new file is provided, upload it and update
              const fd = new FormData();
              fd.append('file', formData.file);
              fd.append('title', formData.title);
              fd.append('description', formData.description || '');
              fd.append('order', formData.order ?? editingItem.order);
              fd.append('isFree', formData.isFree ?? editingItem.isFree);
              fd.append('isMaterial', isMaterial ? 'true' : (editingItem.isMaterial ? 'true' : 'false'));
              await updatePdfWithFile(editingItem._id, fd);
            } else {
              // No new file, just update metadata
              await updatePdf(editingItem._id, {
                title: formData.title,
                description: formData.description,
                order: formData.order ?? editingItem.order,
                isFree: formData.isFree ?? editingItem.isFree,
                isMaterial: isMaterial || editingItem.isMaterial || false
              });
            }
          }
        } else if (contentType === 'test') {
          // Mock tests are PDFs with isMockTest: true
          if (!editingItem._id || editingItem._id.includes('mock-test-')) {
            if (formData.file) {
              const fd = new FormData();
              fd.append('file', formData.file);
              fd.append('title', formData.title);
              fd.append('description', formData.description || '');
              fd.append('order', formData.order || 0);
              fd.append('isFree', formData.isFree || false);
              fd.append('isMockTest', 'true');
              if (formData.mockTest) fd.append('mockTest', formData.mockTest);
              if (formData.totalMarks != null) fd.append('totalMarks', formData.totalMarks);
              if (formData.timeLimitMin != null) fd.append('timeLimitMin', formData.timeLimitMin);
              await uploadPdf(currentSubjectForContent._id, fd);
            } else {
              await createPdf(currentSubjectForContent._id, {
                title: formData.title,
                description: formData.description,
                order: formData.order || 0,
                isFree: formData.isFree || false,
                isMockTest: true,
                mockTest: formData.mockTest || undefined,
                totalMarks: formData.totalMarks,
                timeLimitMin: formData.timeLimitMin
              });
            }
          } else {
            if (formData.file) {
              const fd = new FormData();
              fd.append('file', formData.file);
              fd.append('title', formData.title);
              fd.append('description', formData.description || '');
              fd.append('order', formData.order ?? editingItem.order);
              fd.append('isFree', formData.isFree ?? editingItem.isFree);
              fd.append('isMockTest', 'true');
              if (formData.mockTest) fd.append('mockTest', formData.mockTest);
              if (formData.totalMarks != null) fd.append('totalMarks', formData.totalMarks);
              if (formData.timeLimitMin != null) fd.append('timeLimitMin', formData.timeLimitMin);
              await updatePdfWithFile(editingItem._id, fd);
            } else {
              await updatePdf(editingItem._id, {
                title: formData.title,
                description: formData.description,
                order: formData.order ?? editingItem.order,
                isFree: formData.isFree ?? editingItem.isFree,
                isMockTest: true,
                mockTest: formData.mockTest || undefined,
                totalMarks: formData.totalMarks,
                timeLimitMin: formData.timeLimitMin
              });
            }
          }
        }
        await fetchContentForSubject(currentSubjectForContent._id);
      } else {
        // CREATING NEW CONTENT - no videoId needed
        console.log('Creating new content:', { contentType, hasFile: !!formData.file });
        
        if (contentType === 'video') {
          // New video upload - no videoId required
          if (!formData.file) {
            throw new Error('Video file is required to create a new video');
          }
          const uploadFormData = new FormData();
          uploadFormData.append('video', formData.file);
          uploadFormData.append('subject', currentSubjectForContent._id);
          uploadFormData.append('title', formData.title);
          uploadFormData.append('description', formData.description);
          uploadFormData.append('order', formData.order || 0);
          uploadFormData.append('isFree', formData.isFree || false);
          await uploadVideo(currentSubjectForContent._id, uploadFormData);
        } else if (contentType === 'pdf' || contentType === 'material') {
          // Handle both 'pdf' and 'material' content types
          // Materials are PDFs with isMaterial: true
          const isMaterial = contentType === 'material' || formData.isMaterial;
          
          if (formData.file) {
            const fd = new FormData();
            fd.append('file', formData.file);
            fd.append('title', formData.title);
            fd.append('description', formData.description || '');
            fd.append('order', formData.order || 0);
            fd.append('isFree', formData.isFree || false);
            fd.append('isMaterial', isMaterial ? 'true' : 'false');
            await uploadPdf(currentSubjectForContent._id, fd);
          } else {
            await createPdf(currentSubjectForContent._id, {
              title: formData.title,
              description: formData.description,
              order: formData.order || 0,
              isFree: formData.isFree || false,
              isMaterial: isMaterial || false
            });
          }
        } else if (contentType === 'test') {
          if (formData.file) {
            const fd = new FormData();
            fd.append('file', formData.file);
            fd.append('title', formData.title);
            fd.append('description', formData.description || '');
            fd.append('order', formData.order || 0);
            fd.append('isFree', formData.isFree || false);
            fd.append('isMockTest', 'true');
            if (formData.mockTest) fd.append('mockTest', formData.mockTest);
            if (formData.totalMarks != null) fd.append('totalMarks', formData.totalMarks);
            if (formData.timeLimitMin != null) fd.append('timeLimitMin', formData.timeLimitMin);
            await uploadPdf(currentSubjectForContent._id, fd);
          } else {
            await createPdf(currentSubjectForContent._id, {
              title: formData.title,
              description: formData.description,
              order: formData.order || 0,
              isFree: formData.isFree || false,
              isMockTest: true,
              mockTest: formData.mockTest || undefined,
              totalMarks: formData.totalMarks,
              timeLimitMin: formData.timeLimitMin
            });
          }
        }
      }
      
      setMessage({ type: 'success', text: `${contentType} ${isEdit ? 'updated' : 'created'} successfully!` });
      setShowContentForm(false);
      setEditingItem(null);
      setCurrentSubjectForContent(null);
      await fetchContentForSubject(currentSubjectForContent._id);
      await fetchContentStats();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || `Failed to save ${contentType}` });
      return { success: false, error: error.message };
    }
  };

  // Delete handlers
  const handleDeleteSubject = async (subjectId) => {
    const content = subjectContent[subjectId];
    const totalContent = (content?.videos?.length || 0) + 
                         (content?.pdfs?.length || 0) + 
                         (content?.mockTests?.length || 0);
    
    let confirmMessage = 'Are you sure you want to delete this subject?';
    if (totalContent > 0) {
      confirmMessage += `\n\nThis will permanently delete ${totalContent} content item(s).\n\nThis action cannot be undone!`;
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteSubject(subjectId);
        setMessage({ type: 'success', text: 'Subject deleted successfully!' });
        await fetchAllSubjects();
        await fetchContentStats();
        setExpandedSubjects(prev => {
          const newExpanded = new Set(prev);
          newExpanded.delete(subjectId);
          return newExpanded;
        });
        setSubjectContent(prev => {
          const newContent = { ...prev };
          delete newContent[subjectId];
          return newContent;
        });
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Failed to delete subject' });
      }
    }
  };

  const handleDeleteExam = async (examId) => {
    if (window.confirm('Are you sure you want to delete this exam? This will unlink all associated subjects but will not delete them.')) {
      try {
        await deleteExam(examId);
        setMessage({ type: 'success', text: 'Exam deleted successfully!' });
        await fetchExams();
        await fetchContentStats();
        setExpandedExams(prev => {
          const newExpanded = new Set(prev);
          newExpanded.delete(examId);
          return newExpanded;
        });
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Failed to delete exam' });
      }
    }
  };

  const handleDeleteContent = async (contentId, type, subjectId) => {
    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      try {
        if (type === 'video') {
          await deleteVideo(contentId);
        } else if (type === 'pdf') {
          await deletePdf(contentId);
        } else if (type === 'test') {
          await deletePdf(contentId);
        }
        setMessage({ type: 'success', text: `${type} deleted successfully!` });
        await fetchContentForSubject(subjectId);
        await fetchContentStats();
      } catch (error) {
        setMessage({ type: 'error', text: error.message || `Failed to delete ${type}` });
      }
    }
  };

  // Form handlers
  const openSubjectForm = (subject = null) => {
    setEditingItem(subject);
    setShowSubjectForm(true);
  };

  const openExamForm = (exam = null) => {
    setEditingItem(exam);
    setShowExamForm(true);
  };

  const openLinkForm = (exam) => {
    setSelectedExam(exam);
    setShowLinkForm(true);
  };

  const openSubjectLinkForm = (subject) => {
    setSelectedSubject(subject);
    setShowSubjectLinkForm(true);
  };

  const openContentForm = async (subject, type, content = null) => {
    setCurrentSubjectForContent(subject);
    
    // Only treat as editing if content exists AND has _id
    // If content exists but has no _id, treat it as adding new content
    if (content && content._id) {
      // EDITING EXISTING CONTENT - has _id
      let contentData = { 
        ...content, 
        contentType: type,
        _id: content._id // Explicitly preserve _id
      };
      
      // If editing a video, fetch the video stream URL
      if (type === 'video' && content._id) {
        try {
          const streamResponse = await getVideoStreamUrl(content._id);
          if (streamResponse.success && streamResponse.data?.streamUrl) {
            contentData.fileUrl = streamResponse.data.streamUrl;
          }
        } catch (error) {
          console.error('Error fetching video stream URL:', error);
          // Continue without fileUrl if fetch fails - user can still edit other fields
        }
      }
      
      // For PDFs, ensure fileUrl is included (it should already be in content from API)
      if (type === 'pdf' && content._id) {
        // fileUrl should already be in content from getAllPdfs response
        // If it's missing, log a warning
        if (!contentData.fileUrl && content.fileUrl) {
          contentData.fileUrl = content.fileUrl;
        }
        // Ensure fileName and fileSize are also included
        if (content.fileName) {
          contentData.fileName = content.fileName;
        }
        if (content.fileSize) {
          contentData.fileSize = content.fileSize;
        }
        console.log('PDF data for editing:', { 
          _id: contentData._id, 
          fileUrl: contentData.fileUrl, 
          fileName: contentData.fileName,
          fileSize: contentData.fileSize,
          fullContent: content
        });
      }
      
      console.log('Opening content form for EDITING:', { type, _id: contentData._id, contentData });
      setEditingItem(contentData);
    } else {
      // ADDING NEW CONTENT - no content or no _id
      console.log('Opening content form for ADDING NEW:', { type, hasContent: !!content, contentHasId: !!(content && content._id) });
      setEditingItem({ contentType: type });
    }
    setShowContentForm(true);
  };

  // Fetch all materials (Material entity)
  const fetchMaterialsList = async () => {
    try {
      setLoading(true);
      const response = await getMaterials();
      setMaterials(response.data || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
      setMessage({ type: 'error', text: 'Failed to fetch materials' });
    } finally {
      setLoading(false);
    }
  };

  // Toggle material expansion
  const toggleMaterialExpansion = async (materialId) => {
    const newExpanded = new Set(expandedMaterials);
    if (newExpanded.has(materialId)) {
      newExpanded.delete(materialId);
    } else {
      newExpanded.add(materialId);
      // Fetch content for all subjects in this material when expanding
      // Find subjects that belong to this material by checking the material's subjects array
      const material = materials.find(m => m._id === materialId);
      const materialSubjects = material ? subjects.filter(s => 
        (material.subjects || []).some(subjId => (subjId._id || subjId) === s._id)
      ) : [];
      await Promise.all(
        materialSubjects.map(subject => {
          if (!subjectContent[subject._id]) {
            return fetchContentForSubject(subject._id, false);
          }
          return Promise.resolve();
        })
      );
    }
    setExpandedMaterials(newExpanded);
  };

  const handleMaterialSubmit = async (formData, isEdit = false) => {
    try {
      if (isEdit && editingItem?._id) {
        await updateMaterial(editingItem._id, formData);
        setMessage({ type: 'success', text: 'Material updated successfully!' });
      } else {
        await createMaterial(formData);
        setMessage({ type: 'success', text: 'Material created successfully!' });
      }
      setShowMaterialForm(false);
      setEditingItem(null);
      await fetchMaterialsList();
      await fetchContentStats();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save material' });
      return { success: false, error: error.message };
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (window.confirm('Are you sure you want to delete this material? This will unlink subjects but not delete them.')) {
      try {
        await deleteMaterial(materialId);
        setMessage({ type: 'success', text: 'Material deleted successfully!' });
        await fetchMaterialsList();
        await fetchContentStats();
        setExpandedMaterials(prev => {
          const next = new Set(prev);
          next.delete(materialId);
          return next;
        });
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Failed to delete material' });
      }
    }
  };

  // Fetch all mock test packages
  const fetchMockTestsList = async () => {
    try {
      setLoading(true);
      const response = await getMockTests();
      setMockTests(response.data || []);
    } catch (error) {
      console.error('Error fetching mock tests:', error);
      setMessage({ type: 'error', text: 'Failed to fetch mock test packages' });
    } finally {
      setLoading(false);
    }
  };

  const toggleMockTestExpansion = async (mockTestId) => {
    const newExpanded = new Set(expandedMockTests);
    if (newExpanded.has(mockTestId)) {
      newExpanded.delete(mockTestId);
    } else {
      newExpanded.add(mockTestId);
      // Find subjects that belong to this mock test package by checking the mockTest's subjects array
      const mockTest = mockTests.find(mt => mt._id === mockTestId);
      const packageSubjects = mockTest ? subjects.filter(s => 
        (mockTest.subjects || []).some(subjId => (subjId._id || subjId) === s._id)
      ) : [];
      await Promise.all(
        packageSubjects.map(subject => {
          if (!subjectContent[subject._id]) {
            return fetchContentForSubject(subject._id, false);
          }
          return Promise.resolve();
        })
      );
    }
    setExpandedMockTests(newExpanded);
  };

  const handleMockTestSubmit = async (formData, isEdit = false) => {
    try {
      if (isEdit && editingItem?._id) {
        await updateMockTest(editingItem._id, formData);
        setMessage({ type: 'success', text: 'Mock test package updated successfully!' });
      } else {
        await createMockTest(formData);
        setMessage({ type: 'success', text: 'Mock test package created successfully!' });
      }
      setShowMockTestForm(false);
      setEditingItem(null);
      await fetchMockTestsList();
      await fetchContentStats();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save mock test package' });
      return { success: false, error: error.message };
    }
  };

  const handleDeleteMockTest = async (mockTestId) => {
    if (window.confirm('Are you sure you want to delete this mock test package? This will unlink subjects but not delete them.')) {
      try {
        await deleteMockTest(mockTestId);
        setMessage({ type: 'success', text: 'Mock test package deleted successfully!' });
        await fetchMockTestsList();
        await fetchContentStats();
        setExpandedMockTests(prev => {
          const next = new Set(prev);
          next.delete(mockTestId);
          return next;
        });
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Failed to delete mock test package' });
      }
    }
  };

  const handleLinkSubjectToMockTestPackages = async (subjectId, linkedPackages) => {
    try {
      setLoading(true);
      const mockTestIds = linkedPackages.map(p => p.mockTestId);
      // Only update MockTest models' subjects arrays (reverse relationship)
      for (const pkg of mockTests) {
        const currentSubjectIds = (pkg.subjects || []).map(s => (typeof s === 'object' ? s._id : s) || s);
        const newSubjectIds = currentSubjectIds.filter(id => id !== subjectId);
        if (mockTestIds.includes(pkg._id)) {
          newSubjectIds.push(subjectId);
        }
        await updateMockTest(pkg._id, { subjects: newSubjectIds });
      }
      setMessage({ type: 'success', text: 'Subject linked to mock test packages successfully!' });
      setShowSubjectMockTestLinkForm(false);
      setSelectedSubject(null);
      await fetchAllSubjects();
      await fetchMockTestsList();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to link subject to mock test packages' });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSubjectsToMockTest = async (mockTestId, linkedSubjects) => {
    try {
      setLoading(true);
      // Only update MockTest model's subjects array (reverse relationship)
      const subjectIds = linkedSubjects.map(s => s.subjectId);
      await updateMockTest(mockTestId, { subjects: subjectIds });
      setMessage({ type: 'success', text: 'Subjects linked to mock test package successfully!' });
      setShowMockTestLinkForm(false);
      setSelectedMockTest(null);
      await fetchAllSubjects();
      await fetchMockTestsList();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to link subjects' });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSubjectToMaterial = async (materialId, linkedSubjects) => {
    try {
      setLoading(true);
      
      // Only update Material model's subjects array (reverse relationship)
      const subjectIds = linkedSubjects.map(s => s.subjectId);
      await updateMaterial(materialId, { subjects: subjectIds });
      
      setMessage({ type: 'success', text: 'Subjects linked to material successfully!' });
      setShowMaterialLinkForm(false);
      setSelectedMaterial(null);
      await fetchAllSubjects();
      await fetchMaterialsList();
      return { success: true };
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to link subjects' });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAllSubjects();
    fetchExams();
    fetchMaterialsList();
    fetchMockTestsList();
    fetchContentStats();
  }, []);

  return (
    <>
      <div className="row y-gap-30">
        <div className="col-12">
          <div className="d-flex items-center justify-between mb-30">
            <div>
              <h4 className="text-20 lh-1 fw-500 mb-10">Course Management</h4>
              <p className="text-15 text-light-1">
                Manage subjects first, then create exams and link subjects with pricing
              </p>
            </div>
            <div className="d-flex items-center x-gap-15">
              <ContentStats stats={contentStats} />
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className="col-12">
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} mb-20`}>
              {message.text}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="col-12">
          <div className="tabs-nav bg-white rounded-12 p-15 mb-20">
            <div className="d-flex items-center x-gap-10">
              <button
                onClick={() => setActiveTab('subjects')}
                className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'subjects' ? '#6366f1' : 'transparent',
                  color: activeTab === 'subjects' ? 'white' : '#64748b',
                  fontWeight: activeTab === 'subjects' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <i className="icon-book mr-10"></i>
                Subjects
                <span className="ml-10" style={{ 
                  fontSize: '12px', 
                  background: activeTab === 'subjects' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {subjects.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('exams')}
                className={`tab-button ${activeTab === 'exams' ? 'active' : ''}`}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'exams' ? '#6366f1' : 'transparent',
                  color: activeTab === 'exams' ? 'white' : '#64748b',
                  fontWeight: activeTab === 'exams' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <i className="icon-graduation-cap mr-10"></i>
                Exams
                <span className="ml-10" style={{ 
                  fontSize: '12px', 
                  background: activeTab === 'exams' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {exams.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('materials')}
                className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'materials' ? '#6366f1' : 'transparent',
                  color: activeTab === 'materials' ? 'white' : '#64748b',
                  fontWeight: activeTab === 'materials' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <i className="icon-book mr-10"></i>
                Materials
                <span className="ml-10" style={{ 
                  fontSize: '12px', 
                  background: activeTab === 'materials' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {materials.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('mockTests')}
                className={`tab-button ${activeTab === 'mockTests' ? 'active' : ''}`}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'mockTests' ? '#6366f1' : 'transparent',
                  color: activeTab === 'mockTests' ? 'white' : '#64748b',
                  fontWeight: activeTab === 'mockTests' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <i className="icon-clipboard-list mr-10"></i>
                Mock Tests
                <span className="ml-10" style={{ 
                  fontSize: '12px', 
                  background: activeTab === 'mockTests' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {mockTests.length}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Subjects Panel */}
        {activeTab === 'subjects' && (
          <SubjectPanel
            subjects={subjects}
            exams={exams}
            subjectContent={subjectContent}
            expandedSubjects={expandedSubjects}
            loading={loading}
            onToggleSubject={toggleSubjectExpansion}
            onAddSubject={() => openSubjectForm()}
            onEditSubject={openSubjectForm}
            onDeleteSubject={handleDeleteSubject}
            onAddContent={openContentForm}
            onEditContent={openContentForm}
            onDeleteContent={handleDeleteContent}
            onLinkToExams={openSubjectLinkForm}
            onLinkToMockTests={(subject) => { setSelectedSubject(subject); setShowSubjectMockTestLinkForm(true); }}
          />
        )}

        {/* Exams Panel */}
        {activeTab === 'exams' && (
          <ExamPanel
            exams={exams}
            subjects={subjects}
            subjectContent={subjectContent}
            expandedExams={expandedExams}
            loading={loading}
            onToggleExam={toggleExamExpansion}
            onAddExam={() => openExamForm()}
            onEditExam={openExamForm}
            onDeleteExam={handleDeleteExam}
            onLinkSubjects={openLinkForm}
          />
        )}

        {/* Materials Panel */}
        {activeTab === 'materials' && (
          <MaterialPanel
            materials={materials}
            subjects={subjects}
            subjectContent={subjectContent}
            expandedMaterials={expandedMaterials}
            loading={loading}
            onToggleMaterial={toggleMaterialExpansion}
            onAddMaterial={() => { setEditingItem(null); setShowMaterialForm(true); }}
            onEditMaterial={(m) => { setEditingItem(m); setShowMaterialForm(true); }}
            onDeleteMaterial={handleDeleteMaterial}
            onLinkSubjects={(m) => { setSelectedMaterial(m); setShowMaterialLinkForm(true); }}
          />
        )}

        {/* Mock Tests Panel */}
        {activeTab === 'mockTests' && (
          <MockTestPanel
            mockTests={mockTests}
            subjects={subjects}
            subjectContent={subjectContent}
            expandedMockTests={expandedMockTests}
            loading={loading}
            onToggleMockTest={toggleMockTestExpansion}
            onAddMockTest={() => { setEditingItem(null); setShowMockTestForm(true); }}
            onEditMockTest={(m) => { setEditingItem(m); setShowMockTestForm(true); }}
            onDeleteMockTest={handleDeleteMockTest}
            onLinkSubjects={(m) => { setSelectedMockTest(m); setShowMockTestLinkForm(true); }}
          />
        )}
      </div>

      {/* Modals */}
      {showSubjectForm && (
        <SubjectForm
          initialData={editingItem}
          onSubmit={handleSubjectSubmit}
          onClose={() => {
            setShowSubjectForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showExamForm && (
        <ExamForm
          initialData={editingItem}
          onSubmit={handleExamSubmit}
          onClose={() => {
            setShowExamForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showLinkForm && selectedExam && (
        <ExamSubjectLinkForm
          exam={selectedExam}
          subjects={subjects}
          onSubmit={handleLinkSubjects}
          onClose={() => {
            setShowLinkForm(false);
            setSelectedExam(null);
          }}
        />
      )}

      {showContentForm && (
        <ContentForm
          initialData={editingItem}
          subject={currentSubjectForContent}
          mockTests={mockTests}
          onSubmit={handleContentSubmit}
          onClose={() => {
            setShowContentForm(false);
            setEditingItem(null);
            setCurrentSubjectForContent(null);
          }}
        />
      )}

      {showSubjectLinkForm && selectedSubject && (
        <SubjectExamLinkForm
          subject={selectedSubject}
          exams={exams}
          onSubmit={handleLinkSubjectToExams}
          onClose={() => {
            setShowSubjectLinkForm(false);
            setSelectedSubject(null);
          }}
        />
      )}

      {showMaterialForm && (
        <MaterialForm
          initialData={editingItem}
          onSubmit={handleMaterialSubmit}
          onClose={() => {
            setShowMaterialForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showMaterialLinkForm && selectedMaterial && (
        <MaterialSubjectLinkForm
          material={selectedMaterial}
          subjects={subjects}
          subjectContent={subjectContent}
          onSubmit={handleLinkSubjectToMaterial}
          onClose={() => {
            setShowMaterialLinkForm(false);
            setSelectedMaterial(null);
          }}
        />
      )}

      {showMockTestForm && (
        <MockTestForm
          initialData={editingItem}
          onSubmit={handleMockTestSubmit}
          onClose={() => {
            setShowMockTestForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showMockTestLinkForm && selectedMockTest && (
        <MockTestSubjectLinkForm
          mockTest={selectedMockTest}
          subjects={subjects}
          subjectContent={subjectContent}
          onSubmit={handleLinkSubjectsToMockTest}
          onClose={() => {
            setShowMockTestLinkForm(false);
            setSelectedMockTest(null);
          }}
        />
      )}

      {showSubjectMockTestLinkForm && selectedSubject && (
        <SubjectMockTestLinkForm
          subject={selectedSubject}
          mockTests={mockTests}
          onSubmit={handleLinkSubjectToMockTestPackages}
          onClose={() => {
            setShowSubjectMockTestLinkForm(false);
            setSelectedSubject(null);
          }}
        />
      )}
    </>
  );
};

export default CourseManagement;
